/**
 * Header Component
 * Application header with user profile, settings, and logout
 */

import {
  Menu,
  Settings,
  MessageSquare,
  Bot,
  Shield,
  HelpCircle,
} from 'lucide-react';
import { Avatar, LogoutButton } from '@/components';
import { cn } from '@/utils/classNames';
import { useAdmin } from '@/context/AdminContext';
import type { User } from '@/types';

export interface HeaderProps {
  user?: User | null;
  onMenuClick?: () => void;
  onOpenSettings?: () => void;
  onOpenHelp?: () => void;
  onOpenAdmin?: () => void;
  onNavigateToAgents?: () => void;
  onNavigateToChat?: () => void;
  currentView?: 'chat' | 'agents';
  className?: string;
}

export function Header({
  user,
  onMenuClick,
  onOpenSettings,
  onOpenHelp,
  onOpenAdmin,
  onNavigateToAgents,
  onNavigateToChat,
  currentView = 'chat',
  className,
}: HeaderProps) {
  const { config, isAdmin } = useAdmin();

  return (
    <header
      className={cn(
        'flex items-center justify-between',
        'border-b border-border-default bg-bg-primary',
        'px-4 py-3 md:px-6',
        className
      )}
    >
      {/* Left side - Menu button (mobile) and Logo */}
      <div className="flex items-center gap-4">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className={cn(
              'rounded-lg p-2 text-text-secondary',
              'hover:bg-bg-secondary transition-colors',
              'md:hidden'
            )}
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}

        <div className="flex items-center gap-3">
          {config.branding.headerLogoUrl ? (
            <img
              src={config.branding.headerLogoUrl}
              alt="Logo"
              className="h-8 w-8 rounded-lg object-cover"
            />
          ) : (
            <div
              className="h-8 w-8 rounded-lg bg-stellar-primary
              flex items-center justify-center"
            >
              <span className="text-white font-bold text-lg">S</span>
            </div>
          )}
          <div className="hidden sm:block">
            <h1 className="text-xl font-bold text-stellar-primary">
              {config.branding.headerTitle}
            </h1>
            {config.branding.headerSubtitle && (
              <p className="text-xs text-text-tertiary">
                {config.branding.headerSubtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Center - Navigation (Admin Only) */}
      {user && isAdmin && (
        <div className="hidden md:flex items-center gap-2 rounded-lg border border-border-default bg-bg-primary p-1">
          <button
            onClick={onNavigateToChat}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stellar-primary focus-visible:ring-offset-2',
              currentView === 'chat'
                ? 'bg-stellar-primary text-white'
                : 'text-text-secondary hover:bg-bg-secondary'
            )}
          >
            <MessageSquare className="h-4 w-4" />
            Chat
          </button>
          <button
            onClick={onNavigateToAgents}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stellar-primary focus-visible:ring-offset-2',
              currentView === 'agents'
                ? 'bg-stellar-primary text-white'
                : 'text-text-secondary hover:bg-bg-secondary'
            )}
          >
            <Bot className="h-4 w-4" />
            Agents
          </button>
        </div>
      )}

      {/* Right side - User profile, settings, and logout */}
      {user && (
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-text-primary">
                {user.name}
              </p>
              <p className="text-xs text-text-secondary">
                {user.email}
              </p>
            </div>
            <Avatar src={user.avatar} alt={user.name} size="md" />
          </div>

          <div className="md:hidden">
            <Avatar src={user.avatar} alt={user.name} size="sm" />
          </div>

          {/* Admin button - only for admins */}
          {isAdmin && onOpenAdmin && (
            <button
              onClick={onOpenAdmin}
              className={cn(
                'rounded-lg p-2 text-stellar-primary',
                'hover:bg-bg-secondary transition-colors',
                'border border-stellar-primary/20'
              )}
              aria-label="Open admin console"
              title="Admin Console"
            >
              <Shield className="h-5 w-5" />
            </button>
          )}

          {/* Help / User guide button */}
          {onOpenHelp && (
            <button
              onClick={onOpenHelp}
              className={cn(
                'rounded-lg p-2 text-text-secondary',
                'hover:bg-bg-secondary transition-colors'
              )}
              aria-label="Open user guide"
              title="User guide"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          )}

          {/* Settings button */}
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className={cn(
                'rounded-lg p-2 text-text-secondary',
                'hover:bg-bg-secondary transition-colors'
              )}
              aria-label="Open settings"
              title="Settings"
            >
              <Settings className="h-5 w-5" />
            </button>
          )}

          <LogoutButton variant="ghost" size="sm" />
        </div>
      )}
    </header>
  );
}
